import { useState, useMemo } from 'react';
import { useCoverage, type StateCoverage } from '@/api/hooks';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { GlobeSpinner } from '@/components/ui/globe-spinner';
import { PageBanner } from '@/components/layout/PageBanner';
import { SortableHeader } from '@/components/ui/sortable-header';
import { TablePager } from '@/components/ui/table-pager';
import { Progress } from '@/components/ui/progress';
import { useClientSort } from '@/hooks/use-client-sort';
import { formatNumber } from '@/lib/utils';
import { ArrowLeft, BarChart3 } from 'lucide-react';

interface CountyRow {
  fips5: string;
  county_name: string;
  our_parcel_count: number | null;
  census_housing_units: number | null;
  coverage_pct: number | null;
  has_state_bulk: boolean;
  quality_score: number | null;
}

const qualityColor = (score: number | null) => {
  if (score == null) return 'text-muted-foreground';
  if (score < 0.5) return 'text-red-500';
  if (score < 0.8) return 'text-amber-500';
  return 'text-emerald-500';
};

const coverageColor = (pct: number) => {
  if (pct < 20) return 'oklch(60% 0.22 25)';
  if (pct < 50) return 'oklch(70% 0.16 85)';
  return 'oklch(65% 0.18 145)';
};

const PAGE_SIZE = 20;

const STATE_SORT_FIELDS: Record<string, (s: StateCoverage) => string | number | null | undefined> = {
  state_abbr: s => s.state_abbr?.trim() || '',
  total_counties: s => s.total_counties,
  total_parcels: s => s.total_parcels,
  total_housing_units: s => s.total_housing_units,
  avg_coverage: s => s.avg_coverage,
  with_state_bulk: s => s.with_state_bulk,
  with_county_gis: s => s.with_county_gis,
};

const COUNTY_SORT_FIELDS: Record<string, (c: CountyRow) => string | number | null | undefined> = {
  fips5: c => c.fips5?.trim() || '',
  county_name: c => c.county_name || '',
  our_parcel_count: c => c.our_parcel_count,
  census_housing_units: c => c.census_housing_units,
  coverage_pct: c => c.coverage_pct,
  has_state_bulk: c => c.has_state_bulk ? 1 : 0,
  quality_score: c => c.quality_score,
};

export default function CoveragePage() {
  const [selectedState, setSelectedState] = useState<string | undefined>();
  const [stateFilter, setStateFilter] = useState('');
  const { data, isLoading } = useCoverage(selectedState);

  const stateFilterFn = useMemo(() => {
    if (!stateFilter) return undefined;
    const f = stateFilter.toUpperCase();
    return (s: StateCoverage) => s.state_abbr.trim().includes(f);
  }, [stateFilter]);

  const stateSort = useClientSort<StateCoverage>({
    data: data?.states,
    defaultField: 'state_abbr',
    defaultDir: 'asc',
    fields: STATE_SORT_FIELDS,
    pageSize: PAGE_SIZE,
    filter: stateFilterFn,
  });

  const countySort = useClientSort<CountyRow>({
    data: (data?.counties as CountyRow[] | null) ?? undefined,
    defaultField: 'county_name',
    defaultDir: 'asc',
    fields: COUNTY_SORT_FIELDS,
    pageSize: PAGE_SIZE,
  });

  return (
    <div className="space-y-6 animate-fade-up p-1">
      <PageBanner>
        <div className="flex items-center gap-3">
          {selectedState && (
            <div className="banner-chip">
              <Button size="sm" variant="outline" onClick={() => setSelectedState(undefined)} className="gap-1">
                <ArrowLeft className="h-4 w-4" /> All States
              </Button>
            </div>
          )}
          <div className="banner-chip">
            <h1 className="text-2xl font-bold banner-title">
              {selectedState ? `${selectedState} Coverage` : 'Coverage by State'}
            </h1>
          </div>
        </div>

        {!selectedState && (
          <div className="banner-chip">
            <label className="text-sm font-medium text-muted-foreground">Filter</label>
            <Input
              placeholder="State..."
              value={stateFilter}
              onChange={e => { setStateFilter(e.target.value.toUpperCase()); stateSort.setPage(1); }}
              className="w-32 bg-background/90"
            />
          </div>
        )}
      </PageBanner>

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-700 dark:text-blue-400" />
            <span className="text-blue-700 dark:text-blue-400">{selectedState ? `County Detail` : `State Summary`}</span>
            <Badge variant="info" className="ml-1">{selectedState ? countySort.total : stateSort.total}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <GlobeSpinner label="Loading coverage data..." />
          ) : !selectedState ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <SortableHeader label="State" field="state_abbr" currentField={stateSort.sortField} currentDir={stateSort.sortDir} onSort={stateSort.toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Counties" field="total_counties" currentField={stateSort.sortField} currentDir={stateSort.sortDir} onSort={stateSort.toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Our Parcels" field="total_parcels" currentField={stateSort.sortField} currentDir={stateSort.sortDir} onSort={stateSort.toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Census Units" field="total_housing_units" currentField={stateSort.sortField} currentDir={stateSort.sortDir} onSort={stateSort.toggleSort} />
                    </TableHead>
                    <TableHead className="w-[200px]">
                      <SortableHeader label="Coverage" field="avg_coverage" currentField={stateSort.sortField} currentDir={stateSort.sortDir} onSort={stateSort.toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="State Bulk" field="with_state_bulk" currentField={stateSort.sortField} currentDir={stateSort.sortDir} onSort={stateSort.toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="County GIS" field="with_county_gis" currentField={stateSort.sortField} currentDir={stateSort.sortDir} onSort={stateSort.toggleSort} />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stateSort.paged.map((s, idx) => {
                    const covPct = s.avg_coverage * 100;
                    return (
                      <TableRow
                        key={s.state_abbr}
                        className={`cursor-pointer hover:bg-accent/50 ${idx % 2 === 0 ? '' : 'bg-table-row-even'}`}
                        onClick={() => setSelectedState(s.state_abbr.trim())}
                      >
                        <TableCell className="font-mono font-semibold">{s.state_abbr.trim()}</TableCell>
                        <TableCell className="text-right">{s.total_counties}</TableCell>
                        <TableCell className="text-right font-mono">{formatNumber(s.total_parcels)}</TableCell>
                        <TableCell className="text-right font-mono">{formatNumber(s.total_housing_units)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Progress
                              value={covPct}
                              max={100}
                              color={coverageColor(covPct)}
                              className="h-2 flex-1"
                            />
                            <span className="text-xs font-mono w-12 text-right">{covPct.toFixed(1)}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {s.with_state_bulk > 0
                            ? <Badge variant="success">{s.with_state_bulk}</Badge>
                            : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell>
                          {s.with_county_gis > 0
                            ? <Badge variant="info">{s.with_county_gis}</Badge>
                            : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePager page={stateSort.page} onPageChange={stateSort.setPage} total={stateSort.total} pageSize={PAGE_SIZE} />
            </>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <SortableHeader label="FIPS" field="fips5" currentField={countySort.sortField} currentDir={countySort.sortDir} onSort={countySort.toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="County" field="county_name" currentField={countySort.sortField} currentDir={countySort.sortDir} onSort={countySort.toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Our Parcels" field="our_parcel_count" currentField={countySort.sortField} currentDir={countySort.sortDir} onSort={countySort.toggleSort} />
                    </TableHead>
                    <TableHead className="text-right">
                      <SortableHeader label="Census Units" field="census_housing_units" currentField={countySort.sortField} currentDir={countySort.sortDir} onSort={countySort.toggleSort} />
                    </TableHead>
                    <TableHead className="w-[200px]">
                      <SortableHeader label="Coverage" field="coverage_pct" currentField={countySort.sortField} currentDir={countySort.sortDir} onSort={countySort.toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="State Bulk" field="has_state_bulk" currentField={countySort.sortField} currentDir={countySort.sortDir} onSort={countySort.toggleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Quality" field="quality_score" currentField={countySort.sortField} currentDir={countySort.sortDir} onSort={countySort.toggleSort} />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {countySort.paged.map((c, idx) => {
                    const covPct = (c.coverage_pct ?? 0) * 100;
                    return (
                      <TableRow key={c.fips5} className={idx % 2 === 0 ? '' : 'bg-table-row-even'}>
                        <TableCell className="font-mono text-xs">{c.fips5?.trim()}</TableCell>
                        <TableCell className="font-medium">{c.county_name}</TableCell>
                        <TableCell className="text-right font-mono">{formatNumber(c.our_parcel_count)}</TableCell>
                        <TableCell className="text-right font-mono">{formatNumber(c.census_housing_units)}</TableCell>
                        <TableCell>
                          {c.coverage_pct != null ? (
                            <div className="flex items-center gap-3">
                              <Progress
                                value={covPct}
                                max={100}
                                color={coverageColor(covPct)}
                                className="h-2 flex-1"
                              />
                              <span className="text-xs font-mono w-12 text-right">{covPct.toFixed(1)}%</span>
                            </div>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{c.has_state_bulk ? <Badge variant="success">Yes</Badge> : '-'}</TableCell>
                        <TableCell className={`font-mono font-semibold ${qualityColor(c.quality_score)}`}>
                          {c.quality_score?.toFixed(2) ?? '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <TablePager page={countySort.page} onPageChange={countySort.setPage} total={countySort.total} pageSize={PAGE_SIZE} />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
